/**
 * PaidCreditsCard — dedicated Dashboard resource card for the
 * carryover wallet (Stripe purchases, referral rewards, course
 * stipends, course completion rewards). Never resets with the
 * billing cycle so this is the member's "vault" balance.
 *
 * Sits distinct from `AICreditsWidget` which shows the monthly pool.
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Vault, Plus, TrendingUp } from 'lucide-react';
import PurchaseCreditsModal from './PurchaseCreditsModal';

export default function PaidCreditsCard() {
  const { API } = useContext(AuthContext);
  const [carryover, setCarryover] = useState(0);
  const [ready, setReady] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const prevCarryover = useRef(null);   // null on first load → no phantom toast
  const justDeposited = useRef(false);

  const load = async () => {
    try {
      const [balRes, histRes] = await Promise.all([
        axios.get(`${API}/me/ai-credits`),
        axios.get(`${API}/me/ai-credits/history?kind=purchases&limit=5`).catch(() => ({ data: { rows: [] } })),
      ]);
      const next = Math.max(0, balRes.data?.carryover ?? 0);
      // Deposit detection — carryover grew since last poll. Fires
      // confetti + toast so the reward feels tangible. Skips the very
      // first load so returning members don't get a phantom celebration.
      if (prevCarryover.current !== null && next > prevCarryover.current) {
        const delta = next - prevCarryover.current;
        try {
          confetti({
            particleCount: 90,
            spread: 70,
            startVelocity: 32,
            origin: { y: 0.7 },
            colors: ['#F5B841', '#0b1e3f', '#22c55e'],
          });
        } catch (_) {}
        toast.success(`+${delta.toLocaleString()} credits added to your vault`, {
          description: 'These roll over — no expiry.',
        });
        justDeposited.current = true;
        setTimeout(() => { justDeposited.current = false; }, 2200);
      }
      prevCarryover.current = next;
      setCarryover(next);
      setRecentPurchases(histRes.data?.rows || []);
      setReady(true);
    } catch { setReady(true); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [API]);

  if (!ready) return null;

  return (
    <>
      <Card data-testid="paid-credits-card" className="relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1 bg-amber-500" />
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <Vault className="w-3 h-3 text-amber-600" /> Paid credits — never expire
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Purchases + rewards. Rolls over every month.
              </div>
            </div>
          </div>

          <div className="flex items-baseline gap-1.5 mb-2">
            <span
              className={`font-heading text-3xl font-extrabold tracking-tight text-amber-700 dark:text-amber-400 transition-transform ${justDeposited.current ? 'scale-110' : ''}`}
              data-testid="paid-credits-balance"
            >
              {carryover.toLocaleString()}
            </span>
            <span className="text-xs text-muted-foreground">credits in your vault</span>
          </div>

          {recentPurchases.length > 0 && (
            <div className="mt-2 border-t border-border pt-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">
                Recent
              </div>
              <ul className="space-y-0.5">
                {recentPurchases.slice(0, 3).map((r) => (
                  <li key={r.id} className="flex items-center justify-between text-[11px]">
                    <span className="text-foreground/70 truncate mr-2">{r.reason || 'Purchase'}</span>
                    <span className="font-bold text-emerald-700">+{Math.abs(r.cost)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            type="button"
            onClick={() => setPurchaseOpen(true)}
            className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-500/90 text-navy text-xs font-bold py-2 transition-colors"
            data-testid="paid-credits-buy-more"
          >
            <Plus className="w-3 h-3" /> Add credits
          </button>
        </CardContent>
      </Card>
      <PurchaseCreditsModal
        open={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        onPurchased={load}
      />
    </>
  );
}
